import type { TypeListEntry } from '@/types/list/types.list';

type TypeListModel<T> = {
    entries: TypeListEntry<T>[];
    ordered: T[];
};

type Options<T> = {
    items: readonly T[];
    itemKey: (item: T) => string;
    columns?: number;
    groupBy?: (item: T) => string;
    groupOrder?: readonly string[];
    collapsed?: ReadonlySet<string>;
};

const chunk = <T,>(items: T[], size: number): T[][] => {
    const rows: T[][] = [];
    for (let at = 0; at < items.length; at += size) rows.push(items.slice(at, at + size));
    return rows;
};

const bucket = <T,>(items: readonly T[], groupBy: (item: T) => string, order: readonly string[]) => {
    const groups = new Map<string, T[]>();
    for (const key of order) groups.set(key, []);
    for (const item of items) {
        const key = groupBy(item);
        const list = groups.get(key);
        if (list) list.push(item);
        else groups.set(key, [item]);
    }
    return [...groups.entries()].filter(([, list]) => list.length > 0);
};

export function listEntries<T>({ items, itemKey, columns = 1, groupBy, groupOrder = [], collapsed }: Options<T>): TypeListModel<T> {
    const width = Math.max(1, Math.floor(columns) || 1);
    const entries: TypeListEntry<T>[] = [];
    const ordered: T[] = [];

    const push = (list: T[], group: string | null) => {
        let offset = 0;
        for (const row of chunk(list, width)) {
            entries.push({
                kind: 'items',
                key: `${group ?? ''}\u0000${itemKey(row[0])}`,
                group,
                items: row,
                start: ordered.length,
                offset,
                size: list.length,
            });
            ordered.push(...row);
            offset += row.length;
        }
    };

    if (!groupBy) {
        push([...items], null);
        return { entries, ordered };
    }

    for (const [group, list] of bucket(items, groupBy, groupOrder)) {
        const isCollapsed = collapsed?.has(group) ?? false;
        entries.push({ kind: 'group', key: `\u0001${group}`, group, count: list.length, collapsed: isCollapsed, items: list });
        if (!isCollapsed) push(list, group);
    }

    return { entries, ordered };
}

export default listEntries;
